import {
  getAvatarImages,
  ParsedMentionSuggestion,
  MentionPayload,
  ParagraphNode,
  Node,
  Sort,
  FilterOptions,
} from '@vimeo/comments-ui';
import { loadCookie } from '@vimeo/core/libs/cookies';
import { SuggestedUserMentions } from '@vimeo/gctl-api/models/components/schemas/suggested-user-mentions/SuggestedUserMentions';
import { Video, VideoFields } from '@vimeo/gctl-api/models/components/schemas/video/Video';

import {
  PRIVACY_FOLLOW,
  PRIVACY_PASSWORD,
  PRIVACY_PUBLIC,
  PRIVACY_USER,
  VideoPrivacy,
} from './constants';

/**
 * Get resource id from single level resource uri (i.e - /video/1234 --> 1234, comments/2442/reply/987 --> 987)
 */
export const idFromUri = (uri: string | undefined | null): number => {
  return uri ? parseInt(uri.substring(uri.lastIndexOf('/') + 1), 10) : 0;
};

export const getUserIdFromUserLink = (uri: string): number => {
  return uri ? Number((uri || '').match(/user(\d+)/)?.[1]) : 0;
};

export const extractDeeplinkNoteId = () => {
  let deeplinkNoteId;

  // PRIVATE deeplink: Seach 'comment' param in the url
  const search = window.location.search;
  const params = new URLSearchParams(search);
  deeplinkNoteId = params.get('comment') as unknown as number;

  // PUBLIC deeplink: Check for hash in case its formatted like "#comment_12345 (public comment deeplinks are formatted like this)
  if (!deeplinkNoteId) {
    const urlHash = window.location.hash;
    const regex = /#comment_(\d+)/;
    const match = urlHash.match(regex);
    deeplinkNoteId = match ? (match[1] as unknown as number) : undefined;
  }
  return deeplinkNoteId;
};

export const extractDeepLinkReplyId = () => {
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const replyId = params.get('reply');
  return replyId ? Number(replyId) : null;
};

/**
 * Get video id from clip request id.
 */

export const getVideoIdFromClipRequestId = (clipRequestId: string): string => {
  return clipRequestId.split(':')[0];
};

/**
 * Get parent comment id from uri (i.e - /video/1234/comments/46734/replies/9684 --> 46734, /video/1234/private_comments/46734/replies/9684 --> 46734)
 */
export const getParentCommentIdFromUri = (uri: string) => {
  const match = uri.match(/\/(?:comments|private_comments)\/(\d+)\//);
  return match ? match[1] : null;
};

const getSecondIdFromUri = (uri?: string) => {
  if (!uri) return null;

  const splitUri = uri.split('/');
  return parseInt(splitUri[4]);
};

export const getUserIdFromUri = (uri?: string) => {
  if (!uri) return null;

  const splitUri = uri.split('/');
  return parseInt(splitUri[2]);
};

export const buildUserMentionSuggestion = (mentionSuggestion: SuggestedUserMentions) => {
  const { email, name, isTeamInvitePending, pictures, uri } = mentionSuggestion;

  let teamUserId, userId, guestUserId;

  if (uri.includes('/team_users/')) {
    teamUserId = getSecondIdFromUri(uri);
  } else if (uri.includes('/guest_users/')) {
    guestUserId = uri.split('/').pop();
  } else if (uri.includes('/users/')) {
    userId = getUserIdFromUri(uri);
  }

  const [src, srcSet] = getAvatarImages(pictures?.sizes);

  const payload: ParsedMentionSuggestion = {
    name: name ?? undefined,
    email: email ?? undefined,
    isPending: isTeamInvitePending ?? undefined,
    teamUserId: teamUserId ?? undefined,
    userId: userId ?? undefined,
    guestUserId: guestUserId ?? undefined,
    src: src,
    srcSet: srcSet,
  };

  return payload;
};

export const EMAIL_REGEX = /^\w+([\+\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/;

export const buildMentionNodePayload = ({
  name,
  email,
  userId,
  teamUserId,
  guestUserId,
}: ParsedMentionSuggestion) => {
  return {
    label: name ?? email,
    userId,
    teamUserId,
    guestUserId,
  } as MentionPayload;
};

export const isInternalFacingPrivacy = (privacy: VideoPrivacy) => {
  return ![PRIVACY_PUBLIC, PRIVACY_PASSWORD, PRIVACY_USER, PRIVACY_FOLLOW].includes(privacy);
};

export const FilterToBPOptionMap: { [key: string]: { copy: string; option: string } } = {
  everyone: { copy: 'Everyone', option: 'everyone' },
  team: { copy: 'Only from Team', option: 'team_only' },
  non_team: { copy: 'Only Guests', option: 'guest_only' },
  mentions_replies: { copy: 'Mentions and Replies', option: 'mentions_replies' },
};

export function matchCommentRichText(lowerCaseQuery: string, richtext?: string) {
  if (richtext) {
    return extractCommentText(richtext).toLowerCase().includes(lowerCaseQuery);
  }
}

function extractCommentText(richtext: string): string {
  const parsedRichtext = JSON.parse(richtext || '');
  return parsedRichtext.content
    ?.map((paragraphNode: ParagraphNode) => {
      return paragraphNode.content
        ?.map((node: Node) => {
          const trimmedNodeText = node.type === 'text' ? node.text.trim() : '';
          const result = node.type === 'mention' ? node.attrs?.label : trimmedNodeText;
          return result ?? '';
        })
        .filter((text: string) => !!text)
        .join(' ');
    })
    .join(' ');
}

export const getGuestUserIdFromUri = (uri?: string) => {
  if (!uri) return null;

  const splitUri = uri.split('/');
  return splitUri[2];
};

export const checkForMention = (richtext: string = '') => {
  if (!isValidJSON(richtext)) {
    return /(^|\W)@\w+(\.\w+)?\b/.test(richtext);
  }
  const richTextJson = JSON.parse(richtext);
  return (
    Array.isArray(richTextJson?.content) &&
    richTextJson.content.some((node: ParagraphNode) =>
      node?.content?.some((innerNode: Node) => innerNode.type === 'mention'),
    )
  );
};

const isValidJSON = (data: string) => {
  try {
    JSON.parse(data);
    return true;
  } catch {
    return false;
  }
};

export const sanitiseSpaceInRichtext = (richtext: string) => {
  let parsedRichtext = JSON.parse(richtext || '');

  const trimFirstNode = (paragraphContent: Node[]) => {
    if (paragraphContent.length === 0) return paragraphContent;

    // filter hardbreak nodes from the start
    while (paragraphContent[0].type === 'hardBreak') {
      paragraphContent.shift();
    }
    // trim the first node text
    if (paragraphContent[0].type === 'text') {
      paragraphContent[0].text = paragraphContent[0].text.trimStart();
      if (!paragraphContent[0].text) {
        paragraphContent.shift();
      }
    }

    return paragraphContent;
  };

  const trimLastNode = (paragraphContent: Node[]) => {
    if (paragraphContent.length === 0) return paragraphContent;
    // filter hardbreak nodes from the end
    while (paragraphContent[paragraphContent.length - 1].type === 'hardBreak') {
      paragraphContent.pop();
    }
    const contentLastIndex = paragraphContent.length - 1;
    // trim the last node text
    if (paragraphContent.length > 0 && paragraphContent[contentLastIndex].type === 'text') {
      paragraphContent[contentLastIndex].text = paragraphContent[contentLastIndex].text.trimEnd();
      if (!paragraphContent[contentLastIndex].text) {
        // if previous node is a mention node , then only add a space at the end, do not pop from array
        if (paragraphContent[contentLastIndex - 1].type === 'mention') {
          paragraphContent[contentLastIndex].text += ' ';
        } else {
          paragraphContent.pop();
        }
      }
    }

    return paragraphContent;
  };

  const paragraphContentList = parsedRichtext.content;

  if (paragraphContentList.length) {
    const firstParagraph = paragraphContentList[0];
    const lastParagraph = paragraphContentList[paragraphContentList.length - 1];

    if (firstParagraph.content) {
      const firstParagraphContent = trimFirstNode(firstParagraph.content);
      parsedRichtext.content[0].content = firstParagraphContent;
    }
    if (lastParagraph.content) {
      const lastParagraphContent = trimLastNode(lastParagraph.content);
      parsedRichtext.content[paragraphContentList.length - 1].content = lastParagraphContent;
    }
  }

  return parsedRichtext && JSON.stringify(parsedRichtext);
};

export const getPublicCommentsQuery = (
  password: string | null,
  sort?: Sort,
  filterOptions?: FilterOptions,
) => {
  return {
    sort: sort?.type,
    direction: sort?.direction,
    password,
    includeDeletedComments: true,
    commented_user_type: ['team', 'non_team'].includes(filterOptions?.userType || '')
      ? filterOptions?.userType
      : undefined,
    mentions_and_replies: filterOptions?.mentionAndReplies ?? undefined,
  };
};

export const getDeepLinkPublicCommentsQuery = (
  deeplinkNoteId: number,
  password: string | null,
  sort?: Sort,
) => ({
  commentId: deeplinkNoteId,
  perPage: 25,
  sort: sort?.type,
  direction: sort?.direction,
  ...(password ? { password } : null),
  includeDeletedComments: true,
});

export const getPublicCommentsCountQuery = (password: string | null) => ({
  password,
  includeDeletedComments: true,
  page: 1,
  perPage: 1,
});

/**
 * Get Showcase password hash stored in browser cookie
 */
export const getShowcasePasswordHashFromCookie = (showcaseId: number) => {
  const name = `${showcaseId}_albumpassword`;
  return loadCookie(name);
};

/**
 * Showcase video call expects fields to be prefixed with "clip."
 */
export const mapToClipFields = (fields: VideoFields[]) => {
  return fields.map((field) => `clip.${field}`) as VideoFields[];
};

/**
 * Showcase video response returns fields under "clip" object.
 */
export const extractClipData = (data: Record<string, unknown>) => {
  const clipData = data?.clip;
  if (!clipData) return undefined;
  return clipData as Video;
};

export const getReviewPasswordHashFromCookie = (reviewId?: string): string | undefined => {
  if (!reviewId) return undefined;
  const name = `${reviewId}_password`;
  return loadCookie(name) ?? undefined;
};

/**
 * Check if a comment belongs to the current user
 * Compares the viewer's user URI or guest user ID with the comment's user information
 */
export const checkIsOwnComment = (
  viewerUserUri?: string,
  guestUserId?: string,
  commentUserUri?: string,
  commentGuestUserUri?: string,
): boolean => {
  if (viewerUserUri && viewerUserUri === commentUserUri) {
    return true;
  }

  if (guestUserId && guestUserId === getGuestUserIdFromUri(commentGuestUserUri)) {
    return true;
  }

  return false;
};
